import { useState, useEffect, useCallback } from 'react';
import { getContextualContent, getArticleBySlug, HelpArticle } from '../content';

interface UseHelpContentResult {
  title: string;
  content: string;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to load help content by key (for contextual help)
 * Content is loaded from the content registry
 */
export function useHelpContent(contentKey: string): UseHelpContentResult {
  const [state, setState] = useState<UseHelpContentResult>({
    title: '',
    content: '',
    loading: true,
    error: null
  });

  useEffect(() => {
    // Simulate async loading for consistency (could be made async for remote content)
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const contextual = getContextualContent(contentKey);
      
      if (contextual) {
        setState({
          title: contextual.title,
          content: contextual.content,
          loading: false,
          error: null
        });
      } else {
        // Try to find as an article
        const article = getArticleBySlug(contentKey);
        if (article) {
          setState({
            title: article.title,
            content: article.content,
            loading: false,
            error: null
          });
        } else {
          setState({
            title: 'Help',
            content: '',
            loading: false,
            error: `Help content not found for: ${contentKey}`
          });
        }
      }
    } catch (err) {
      setState({
        title: 'Error',
        content: '',
        loading: false,
        error: 'Failed to load help content'
      });
    }
  }, [contentKey]);

  return state;
}

/**
 * Hook to load a full help article by slug
 */
export function useHelpArticle(slug: string): {
  article: HelpArticle | null;
  loading: boolean;
  error: string | null;
} {
  const [state, setState] = useState<{
    article: HelpArticle | null;
    loading: boolean;
    error: string | null;
  }>({
    article: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const article = getArticleBySlug(slug);
      
      if (article) {
        setState({
          article,
          loading: false,
          error: null
        });
      } else {
        setState({
          article: null,
          loading: false,
          error: `Article not found: ${slug}`
        });
      }
    } catch (err) {
      setState({
        article: null,
        loading: false,
        error: 'Failed to load article'
      });
    }
  }, [slug]);

  return state;
}

/**
 * Hook for searching help content
 */
export function useHelpSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HelpArticle[]>([]);

  const search = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
    
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    // Import search function
    import('../content').then(({ searchArticles }) => {
      const searchResults = searchArticles(searchQuery);
      setResults(searchResults);
    });
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  return {
    query,
    results,
    search,
    clearSearch,
    hasResults: results.length > 0
  };
}






